import { MapboxUtil } from "maptalks"

/**
 * Extensible registry of maptalks symbol tokens that can drive a Three.js
 * material property from zoom. Each entry compiles a token's `{ stops }`
 * curve into a `zoom -> value` fn and knows how to write that value onto a
 * material. Add a token here (e.g. `polygonFill`) to make it zoom-drivable
 * without touching the applier in IndoorMap.class.ts.
 */
export const ZOOM_STYLE_TOKENS = {
  polygonOpacity: {
    interpolate: MapboxUtil.interpolated,
    setter: (material: any, value: number): void => {
      material.opacity = value
      material.visible = value > 0
    },
  },
  lineOpacity: {
    interpolate: MapboxUtil.interpolated,
    // LineMaterial (maptalks.three's fat-line ShaderMaterial) reads opacity
    // from its own `uniforms.opacity` — THREE's renderer skips the usual
    // material.opacity -> uniform sync for ShaderMaterial instances, so it
    // must be written directly or the outline never visually fades.
    setter: (material: any, value: number): void => {
      material.opacity = value
      if (material.uniforms?.opacity) material.uniforms.opacity.value = value
      material.visible = value > 0
    },
  },

  markerOpacity: {
    interpolate: MapboxUtil.interpolated,
    setter: (material: any, value: number) => {
      material.transparent = true
      material.opacity = value
      material.visible = value > 0
      material.needsUpdate = true
    },
  },
}

/**
 * Scans `symbol` for tokens present in ZOOM_STYLE_TOKENS with a `{ stops }`
 * value, compiles each into a `zoom -> value` fn, and returns a single
 * `apply(zoom)` closure that writes all of them onto `mesh.object3d.material`.
 * The `stops` values are the literal material values at each zoom (e.g.
 * `polygonOpacity.stops` values are opacity, 0..1) — the theme is the single
 * source of truth for the ceiling, not app code.
 *
 * `tokens`, if given, restricts which tokens are compiled for this mesh —
 * needed when a symbol carries stops for multiple sibling meshes (e.g. a
 * section's fill and its separate outline mesh both read from the same
 * symbol, but only `polygonOpacity` should drive the fill and only
 * `lineOpacity` should drive the outline).
 */
export const compileZoomStyle = (
  symbol: Record<string, any> = {},
  mesh: any,
  tokens?: string[]
): ((zoom: number) => void) => {
  const compiled = Object.entries(ZOOM_STYLE_TOKENS)
    .filter(
      ([token]) => (!tokens || tokens.includes(token)) && symbol?.[token]?.stops
    )
    .map(([token, { interpolate, setter }]) => ({
      setter,
      fn: interpolate({ stops: symbol[token].stops }),
    }))

  return (zoom: number): void => {
    const object3d = mesh.object3d
    const material = object3d?.material ?? object3d?.children?.[0]?.material //sprite marker
    if (!material) return
    compiled.forEach(({ setter, fn }) => setter(material, fn(zoom)))
    // Keep pickability in sync with visibility: maptalks.three gates hit-testing
    // on object3d.visible (via isVisible()), not material.visible. Without this
    // an opacity-0 mesh stays pickable and steals clicks from elements below it.
    object3d.visible = material.visible
  }
}
